/**
 * Centralized WebSocket service for real-time event handling
 */

class WebSocketService {
  constructor() {
    this.ws = null
    this.reconnectAttempts = 0
    this.reconnectDelay = 1000
    this.maxReconnectDelay = 30000
    this.reconnectTimer = null
    this.listeners = new Map()
    this.token = null
    this.isConnecting = false

    // Recovery triggers: network back online, or tab visible again after sleep /
    // background suspension. Keeps the HUD and live views working without a page
    // refresh even after long disconnects.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this._recoverConnection())
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this._recoverConnection()
      })
    }
  }

  _recoverConnection() {
    if (this.isConnected() || this.isConnecting || this.reconnectTimer) return
    const token = localStorage.getItem('access_token')
    if (!token) return
    console.log('WebSocket: attempting recovery reconnect')
    this.connect(token)
  }

  /**
   * Connect to WebSocket server
   * @param {string} token - JWT token for authentication
   */
  connect(token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected')
      return
    }

    if (this.isConnecting) {
      console.log('WebSocket connection already in progress')
      return
    }

    // An explicit connect supersedes any scheduled reconnect
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this.token = token
    this.isConnecting = true

    try {
      // Determine WebSocket URL based on environment
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const wsUrl = `${protocol}//${host}/api/ws?token=${encodeURIComponent(token)}`

      console.log('Connecting to WebSocket:', wsUrl.replace(token, '***'))

      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        console.log('WebSocket connected')
        this.isConnecting = false
        this.reconnectAttempts = 0
        this._startKeepAlive()
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('WebSocket received message:', data.event_type || data.type, data)
          this._handleMessage(data)
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error, event.data)
        }
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        this.isConnecting = false
      }

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason)
        this.isConnecting = false
        this._stopKeepAlive()

        // Reconnect on any abnormal closure (indefinitely, capped backoff).
        // Normal closure (1000) comes from disconnect() — do not reconnect.
        if (event.code !== 1000) {
          this._reconnect()
        }
      }
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)
      this.isConnecting = false
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this._stopKeepAlive()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnectAttempts = 0
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }
    this.token = null
  }

  /**
   * Reconnect to WebSocket server
   *
   * Retries indefinitely with capped exponential backoff (1s → 30s). Every
   * attempt re-reads the token from localStorage so a refreshed session token
   * is picked up instead of reconnecting with a stale one.
   */
  _reconnect() {
    if (this.reconnectTimer) return

    const token = localStorage.getItem('access_token')
    if (!token) {
      console.warn('Cannot reconnect: no token available')
      this.token = null
      return
    }
    this.token = token

    this.reconnectAttempts++
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay)

    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.token && !this.isConnected() && !this.isConnecting) {
        this.connect(this.token)
      }
    }, delay)
  }

  /**
   * Start keep-alive ping
   */
  _startKeepAlive() {
    this.keepAliveInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, 30000) // Send ping every 30 seconds
  }

  /**
   * Stop keep-alive ping
   */
  _stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval)
      this.keepAliveInterval = null
    }
  }

  /**
   * Handle incoming WebSocket message
   */
  _handleMessage(data) {
    const eventType = data.event_type || data.type
    console.log(`WebSocket handling event: ${eventType}`, {
      eventType,
      hasListeners: this.listeners.has(eventType),
      listenerCount: (this.listeners.get(eventType) || []).length,
      allListeners: this.listeners.has('*') ? (this.listeners.get('*') || []).length : 0
    })

    // Call all listeners for this event type
    const listeners = this.listeners.get(eventType) || []
    console.log(`Calling ${listeners.length} listener(s) for ${eventType}`)
    listeners.forEach(listener => {
      try {
        listener(data)
      } catch (error) {
        console.error(`Error in WebSocket listener for ${eventType}:`, error)
      }
    })

    // Also call 'all' listeners
    const allListeners = this.listeners.get('*') || []
    allListeners.forEach(listener => {
      try {
        listener(data)
      } catch (error) {
        console.error('Error in WebSocket listener for *:', error)
      }
    })
  }

  /**
   * Subscribe to a specific event type
   * @param {string} eventType - Event type to listen for ('*' for all events)
   * @param {Function} callback - Callback function to call when event is received
   * @returns {Function} Unsubscribe function
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, [])
    }
    this.listeners.get(eventType).push(callback)

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(eventType)
      if (listeners) {
        const index = listeners.indexOf(callback)
        if (index > -1) {
          listeners.splice(index, 1)
        }
      }
    }
  }

  /**
   * Unsubscribe from an event type
   */
  off(eventType, callback) {
    const listeners = this.listeners.get(eventType)
    if (listeners) {
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN
  }
}

// Export singleton instance
const websocketService = new WebSocketService()
export default websocketService
